<template>
  <div class="drop-down dropdown-list">
    <div class="drop-down__selected-container" ref="dropDown" @click="toggleDropDown">
      <div class="drop-down__selected truncate" :data-properties="selectedDataProperties">
        <span class="truncate drop-down__list-item-label" :title="computedSelectedTooltip">{{ computedSelectedVal }}</span>
        <em class="min-w-fit-content"><small class="drop-down__suffix" v-if="computedSelectedSuffix" v-text="computedSelectedSuffix" /></em>
      </div>
      <div :class="['drop-down__icon', showDropDown ? '' : 'collapsed']"></div>
    </div>

    <div class="drop-down__list-container" v-if="showDropDown">
      <ul v-if="props.listData && props.listData.length" class="list no-bullet" role="listbox" :aria-expanded="showDropDown">
        <li v-for="(listItem, index) in props.listData" :key="index" role="option" tabindex="0">
          <div class="drop-down__list-item" :data-properties="getDataProperties(listItem)" @click="selectItemClicked(listItem, index)">
            <span class="drop-down__list-item-label ml-2" :title="listItem.tooltip ? listItem.tooltip : ''">{{ listItem.label }}</span>
            <em class="min-w-fit-content"><small class="drop-down__suffix" v-if="listItem.suffix" v-text="listItem.suffix" /></em>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import '@/assets/css/components/drop-down.scss';
import { withDefaults, ref, computed, onUpdated } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { IDropDownItem } from '../../foundation/interfaces/IDropDownItem';

const props = withDefaults(
  defineProps<{
    listData?: IDropDownItem[];
    defaultToFirstItem?: Boolean;
    selectedItem?: IDropDownItem;
    placeholderText?: string;
  }>(),
  {
    defaultToFirstItem: () => {
      return true;
    },
    placeholderText: 'Select item',
  }
);

// handle when the user clicks outside of the drop down
const dropDown = ref(null);
const selectedValue = ref(props.selectedItem as IDropDownItem);
const showDropDown = ref(false);
const selectionChanged = ref(false);

const emits = defineEmits(['selected-changed']);

const selectedDataProperties = computed(() => {
  if (props.listData && props.listData.length && selectedValue.value)
    return JSON.stringify(props.listData.find((item) => item.value === selectedValue.value.value));
  else return '';
});

const computedSelectedTooltip = computed(() => selectedValue.value?.tooltip || '');
const computedSelectedVal = computed(() => selectedValue.value?.label || props.placeholderText);
const computedSelectedSuffix = computed(() => selectedValue.value?.suffix || '');

function getDataProperties(itemToSerialize: object) {
  return JSON.stringify(itemToSerialize);
}

function selectItemClicked(itemClicked: IDropDownItem, index: number) {
  selectedValue.value = itemClicked;
  showDropDown.value = false;
  selectionChanged.value = true;
  emits('selected-changed', itemClicked, index);
}

function toggleDropDown() {
  showDropDown.value = !showDropDown.value;
}

onUpdated(() => {
  if (selectionChanged.value) return;

  if (props.selectedItem) {
    selectedValue.value = props.selectedItem;
  }

  if (!props.selectedItem && props.defaultToFirstItem) {
    selectedValue.value = props.listData
      ? props.listData[0]
      : {
          label: props.placeholderText,
          value: '',
        };
  }
});

onClickOutside(dropDown, (event: any) => {
  if (event?.offsetX <= event?.target?.clientWidth) {
    showDropDown.value = false;
  }
});
</script>
<style lang="scss" scoped>
.dropdown-list {
  height: 2.5rem;
  width: 100%;
  border-radius: 4px;
  color: $racq-dark-grey;
  border-color: $racq-pale-blue-border;
  background-color: $racq-true-white;
  container-type: size;
}

.drop-down__selected,
.drop-down__list-item {
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 0.5rem 0 0;
  align-items: center;
}

.drop-down__list-item-label {
  line-height: 2;
}

.drop-down__list-item-label {
  line-height: 2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 15rem;
}

@container (min-width: 400px) {
  .drop-down__list-item-label {
    max-width: none;
  }
}
</style>
